import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './AuthPages.css';

/* ─── EYE ICONS ───────────────────────────────────────────── */
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', form);
      login(data, data.token);
      toast.success(`Welcome back, ${data.name}! 👋`);
      const redirectTo = location.state?.from;
      navigate(redirectTo || (data.role === 'admin' ? '/admin' : '/dashboard'));
    } catch (err) {
      if (err.response?.data?.unverified) {
        toast.error('Please verify your email first');
        navigate('/verify-otp', { state: { email: err.response.data.email } });
        return;
      }
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputBase = {
    background: 'var(--bg3)', border: `1.5px solid var(--border)`, borderRadius: 10,
    color: 'var(--text)', fontSize: 15,
    fontFamily: "'Satoshi', 'DM Sans', sans-serif",
    transition: 'all 0.2s', width: '100%', boxSizing: 'border-box',
  };

  return (
    <main className="auth-page">
      <div className="auth-panel">
        <section className="auth-story">
          <div className="auth-story-content">
            <div className="auth-story-kicker">Your learning journey</div>
            <h1>Welcome back.<span>Keep growing.</span></h1>
            <p>Pick up where you left off and keep building skills for the future you want.</p>
            <div className="auth-story-points">
              <span><b aria-hidden="true">✓</b> Learn at your own pace</span>
              <span><b aria-hidden="true">✓</b> Track your courses and certificates</span>
              <span><b aria-hidden="true">✓</b> Get support whenever you need it</span>
            </div>
          </div>
          <div className="auth-story-footer">Learn. Grow. Certify.</div>
        </section>

        <section className="auth-form-panel">
          <div className="auth-form-heading">
            <h2>Sign in</h2>
            <p>Login to continue your learning journey with ThiranX.</p>
          </div>
          <form onSubmit={handleSubmit} className="auth-form-card">
            <div className="form-group">
              <label htmlFor="login-email">Email address</label>
              <input
                id="login-email" name="email" type="email" value={form.email} onChange={handleChange}
                placeholder="you@example.com" autoComplete="email" required
                style={{ ...inputBase, padding: '12px 16px' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password" name="password" type={showPassword ? 'text' : 'password'}
                  value={form.password} onChange={handleChange}
                  placeholder="Enter your password" autoComplete="current-password" required
                  style={{ ...inputBase, padding: '12px 48px 12px 16px' }}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(p => !p)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--muted)', padding: 0, display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              <div className="auth-forgot">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary auth-submit">
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>

            <p className="auth-switch">
              Don’t have an account?
              <Link to="/register">Register here</Link>
            </p>
          </form>
          <Link to="/" className="auth-home-link">← Back to Home</Link>
        </section>
      </div>
    </main>
  );
};

export default Login;
